import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";

/**
 * Query-key prefixes that are safe and useful to persist across reloads.
 * These are the figures the user expects to see on the first frame
 * (balances, prices, FX, currency/network configuration, chart series).
 *
 * Anything not listed here is never written to storage — in particular
 * auth, KYC documents, admin queries and support content.
 */
const PERSISTED_PREFIXES = [
  "environment_wallets",
  "user-trading-wallet",
  "wallet-transferable-assets",
  "price-ticker",
  "price-multi-ticker",
  "price-candles",
  "fiat-exchange-rates",
  "fiat-currencies-usd-rates",
  "token-usd-rates",
  "crypto-networks",
  "fiat-config",
  "fiat-options",
  "platform_settings",
  "token-configs",
  "portfolio-history",
  "market-tickers",
] as const;

export function shouldPersistQuery(query: any): boolean {
  const head = query.queryKey?.[0];
  if (typeof head !== "string") return false;
  if (query.state.status !== "success") return false;
  return PERSISTED_PREFIXES.some((p) => head === p || head.startsWith(p));
}

export const queryPersister = createSyncStoragePersister({
  storage: typeof window !== "undefined" ? window.localStorage : undefined,
  key: "acex-query-cache",
  throttleTime: 1000,
});

/** Bump when cached query shapes change so stale entries are dropped. */
export const QUERY_CACHE_BUSTER = "v2";

/** How long a persisted entry stays usable. */
export const QUERY_CACHE_MAX_AGE = 24 * 60 * 60 * 1000;
